'use client';

type Point = { x: number; y: number };

export function mrLerpPosition(from: Point, to: Point, alpha: number): Point {
  const t = Math.min(1, Math.max(0, alpha));
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
  };
}

export function mrDistance(from: Point, to: Point): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return Math.sqrt(dx * dx + dy * dy);
}

export function mrSmoothNetworkPosition(input: {
  current: Point;
  target: Point;
  speed: number;
  dtMs: number;
  snapDistance?: number;
  teleportDistance?: number;
  catchupMultiplier?: number;
  minStep?: number;
}): Point {
  const distance = mrDistance(input.current, input.target);
  if (!Number.isFinite(distance)) {
    return input.target;
  }

  const snapDistance = input.snapDistance ?? 1.25;
  if (distance <= snapDistance) {
    return input.target;
  }

  if (distance >= (input.teleportDistance ?? 420)) {
    return input.target;
  }

  const dtMs = Math.max(1, Math.min(80, input.dtMs));
  const speed = Math.max(1, input.speed);
  const maxStep = Math.max(input.minStep ?? 0.8, speed * (input.catchupMultiplier ?? 1.22) * (dtMs / 1000));
  return mrLerpPosition(input.current, input.target, maxStep / distance);
}

export function mrIsNetworkMovementActive(input: {
  state?: string | null;
  nowMs: number;
  etaMs?: number | null;
  position: Point;
  target: Point;
  stopLeadMs?: number;
  stopDistance?: number;
}): boolean {
  if (input.state !== 'moving') {
    return false;
  }

  const etaMs = Number(input.etaMs ?? 0);
  const targetDistance = mrDistance(input.position, input.target);
  if (Number.isFinite(etaMs) && etaMs > 0) {
    const stopLeadMs = input.stopLeadMs ?? 50;
    if (input.nowMs >= etaMs - stopLeadMs && targetDistance <= (input.stopDistance ?? 3)) {
      return false;
    }
    if (input.nowMs >= etaMs + stopLeadMs) {
      return false;
    }
  }

  return true;
}
